import api from "./api";

/**
 * Money — BIZ-17's checkout and BIZ-19's transactions.
 *
 * ## The two writes both carry an Idempotency-Key
 *
 * Taking a payment and issuing a refund are the two requests in this portal
 * where a retry that gets through twice costs somebody real money. Both send
 * the header the API requires, and — as with the booking modal — the key
 * belongs to the *attempt*, not the request: it is generated when the form
 * opens and reused across retries, so a dropped connection settles once.
 */

/** A key per attempt. The same generator the booking modal uses. */
export const newIdempotencyKey = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `key-${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** Filters -> query string, shared by the list and the export so they cannot drift. */
const toParams = (filters = {}) => {
  const params = {};

  if (filters.search) params.search = filters.search;
  if (filters.status) params.status = filters.status;
  // Any of these methods; a payment that used a reward also matches loyalty_reward.
  if (filters.methods?.length) params.payment_methods = filters.methods;
  if (filters.settlementStatus) params.settlement_status = filters.settlementStatus;
  if (filters.branch) params.branch = filters.branch;
  // A provider only means something within a branch, as on the page.
  if (filters.branch && filters.staff) params.staff_id = filters.staff;
  if (filters.client) params.client = filters.client;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;

  return params;
};

/**
 * GET /business/payments — BIZ-19.
 *
 * `meta.totals` covers the whole filtered set rather than the page, which is
 * the number somebody opened this screen for.
 */
export const fetchPayments = async ({ page = 1, perPage = 15, filters = {} } = {}) => {
  const { data } = await api.get("/business/payments", {
    params: { ...toParams(filters), page, per_page: perPage },
  });

  return { payments: data.data.payments, meta: data.meta };
};

/** GET /business/payments/{id} — with the refund quote the form needs. */
export const fetchPayment = async (id) => {
  const { data } = await api.get(`/business/payments/${id}`);

  return { payment: data.data.payment, refundQuote: data.data.refund_quote };
};

/**
 * GET /business/payments/export — the filtered transactions as a CSV.
 *
 * Streamed behind an Authorization header, so it cannot be a plain link: the
 * same blob-and-click as the client export.
 */
export const exportPayments = async (filters = {}) => {
  const response = await api.get("/business/payments/export", {
    params: toParams(filters),
    responseType: "blob",
  });

  const disposition = response.headers["content-disposition"] ?? "";
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const name = match ? match[1] : `transactions-${new Date().toISOString().slice(0, 10)}.csv`;

  const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return name;
};

/* -------------------------------------------------------------- checkout */

/**
 * GET the checkout summary for one booking — what it owes and how it can pay.
 *
 * With `{ rewardId }` (claim now) or `{ redemptionId }` (already claimed) the
 * answer also carries `loyalty.quote`: the totals with that reward applied,
 * worked out by the server — or a 422 on that field saying why it cannot be.
 */
export const fetchCheckout = async (appointmentId, { rewardId, redemptionId, lineId } = {}) => {
  const params = {};
  if (rewardId) params.loyalty_reward_id = rewardId;
  if (redemptionId) params.loyalty_redemption_id = redemptionId;
  // A free service: which matching line is the free one (default: the first).
  if (lineId) params.loyalty_line_id = lineId;

  const { data } = await api.get(`/business/appointments/${appointmentId}/checkout`, { params });

  return data.data;
};

/**
 * POST a payment against a booking.
 *
 * The amount is validated server-side against what is genuinely outstanding —
 * this screen shows a number, it does not decide one.
 */
export const takePayment = async (appointmentId, values, idempotencyKey) => {
  const payload = {
    amount: values.amount,
    method: values.method,
  };

  if (values.purpose) payload.purpose = values.purpose;
  // At most one: the server claims / uses it in the same transaction.
  if (values.rewardId) payload.loyalty_reward_id = values.rewardId;
  if (values.redemptionId) payload.loyalty_redemption_id = values.redemptionId;
  if (values.lineId) payload.loyalty_line_id = values.lineId;

  const { data } = await api.post(`/business/appointments/${appointmentId}/payments`, payload, {
    headers: { "Idempotency-Key": idempotencyKey },
  });

  return data.data;
};

/**
 * POST a refund against a payment.
 *
 * A reason is required by the API, not merely encouraged: a refund nobody can
 * explain later is the one that causes trouble.
 */
export const refundPayment = async (paymentId, values, idempotencyKey) => {
  const payload = {
    amount: values.amount,
    reason: values.reason,
  };

  if (values.note) payload.note = values.note;

  const { data } = await api.post(`/business/payments/${paymentId}/refunds`, payload, {
    headers: { "Idempotency-Key": idempotencyKey },
  });

  return data.data;
};
